import React, { FC, useEffect, useRef, useState } from "react";

import { Divider, Skeleton } from "antd";

import { AccruedFee, RiskLevel } from "../../data-types";
import { HTMLLink } from "../active-matters";
import { VpdList } from "../vpd-list";
import InfiniteScroll from "react-infinite-scroll-component";
import { useGpmSettingsCheck } from "~/hooks";
import styles from "./accrued-fee-list.module.css";
import { LoadingSkeleton } from "../../../components/page-loading/skeleton/loading-skeleton";
import { LiveMatters } from "./live-matters";
import { LiveMatterOrderBy } from "../active-matters-status-filter-list";
import { useTaxonomyLabel } from "~/context/taxonomy-context";

const {
  ["container"]: containerClassName,
  ["scrollable-container"]: scrollableContainerClassName,
} = styles as any;

export type AccruedFeeListProps = {
  loading: boolean;
  accruedFees: AccruedFee[];
  link: HTMLLink;
  onShowTimeLogs: (matterId: number) => void;
  onShowReport: (matterId: number) => void;
  onLoadMore: (nextTake: number) => void;
  total: number;
  filterBy: "user" | "organisationalUnit";
  orderBy: LiveMatterOrderBy;
};

export const AccruedFeeList: FC<AccruedFeeListProps> = ({
  loading,
  accruedFees,
  link: Link,
  onShowTimeLogs,
  onShowReport,
  total,
  onLoadMore,
  filterBy,
  orderBy,
}) => {
  const { showGpm, showGpmSlider } = useGpmSettingsCheck();

  const enumValues = Object.values(RiskLevel);

  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    setHeight(ref?.current?.clientHeight ?? 0);
  }, []);

  function sortedRiskLevelComparator(a: AccruedFee, b: AccruedFee): number {
    return enumValues.indexOf(a.actualFeeRiskLevel) - enumValues.indexOf(b.actualFeeRiskLevel);
  }
  function sortedRecentlyAddedComparator(a: AccruedFee, b: AccruedFee): number {
    const dateA = new Date(a.matterCreatedAt);
    const dateB = new Date(b.matterCreatedAt);
    return dateB.getTime() - dateA.getTime();
  }
  function sortedRecentWorkComparator(a: AccruedFee, b: AccruedFee): number {
    const dateA = a.mostRecentTimeEntry ? new Date(a.mostRecentTimeEntry).getTime() : -Infinity;
    const dateB = b.mostRecentTimeEntry ? new Date(b.mostRecentTimeEntry).getTime() : -Infinity;
    return dateB - dateA;
  }

  const hasMore = accruedFees.length < total;

  const dataLength = accruedFees.length;

  const sortFunctions = {
    recentlyAdded: sortedRecentlyAddedComparator,
    recentWork: sortedRecentWorkComparator,
    riskLevel: sortedRiskLevelComparator,
  };

  const sortedItems = accruedFees.slice().sort(sortFunctions[orderBy]);

  const taxonomy = useTaxonomyLabel();

  return (
    <div className={containerClassName}>
      <div ref={ref} id="scrollableDiv" className={scrollableContainerClassName}>
        {loading && <LoadingSkeleton count={10} loading={loading} />}
        <InfiniteScroll
          dataLength={dataLength}
          next={() => {
            return onLoadMore(10);
          }}
          height={height - 1}
          scrollableTarget={"scrollableDiv"}
          hasMore={hasMore}
          endMessage={
            dataLength > 0 && (
              <Divider>
                Showing all{" "}
                {filterBy === "organisationalUnit"
                  ? `your live ${taxonomy.PracticeArea} matters`
                  : "your live matters"}{" "}
              </Divider>
            )
          }
          loader={<Skeleton style={{ padding: 10 }} paragraph={{ rows: 1 }} active />}
        >
          <VpdList
            itemKeyProperty={"matterId"}
            itemTextProperty={"title"}
            items={sortedItems}
            split
            loading={loading}
            labels={{
              emptyText: "No Matters found",
              loadingText: "Loading Matters...",
            }}
            renderItem={(item) => {
              return (
                <LiveMatters
                  filterBy={filterBy}
                  loading={loading}
                  item={item}
                  Link={Link}
                  currencySymbol={item.currencySymbol}
                  onShowTimeLogs={onShowTimeLogs}
                  onShowReport={onShowReport}
                  showGpm={showGpm}
                  showGpmSlider={showGpmSlider}
                />
              );
            }}
          />
        </InfiniteScroll>
      </div>
    </div>
  );
};
